import { PushNotifications, Token } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { supabase } from '@/integrations/supabase/client';
import { getAppType } from '@/hooks/useCapacitorApp';

// Which app is this build? Set via Capacitor config appId.
const detectApp = (): 'pro' | 'couple' => getAppType() === 'pro' ? 'pro' : 'couple';

let signingOut = false;
let registrationWork: Promise<void> = Promise.resolve();
const tokenKey = () => `native-push-token:${detectApp()}`;

const saveTokenToBackend = async (token: string) => {
  if (signingOut) return;
  try {
    await Preferences.set({ key: tokenKey(), value: token });
    const { data: auth } = await supabase.auth.getUser();
    if (signingOut) return;
    const userId = auth?.user?.id;
    if (!userId) {
      console.log('[push] no signed-in user, skipping token save');
      return;
    }
    const platform = (Capacitor.getPlatform() === 'ios' ? 'ios' : 'android') as 'ios' | 'android';
    const app = detectApp();
    const { error } = await supabase.from('native_push_tokens').upsert(
      {
        user_id: userId,
        token,
        platform,
        app,
        last_used_at: new Date().toISOString(),
      },
      { onConflict: 'token,app' },
    );
    if (error) console.error('[push] failed to persist token', error);
  } catch (err) {
    console.error('[push] saveTokenToBackend error', err);
  }
};

export const initializePushNotifications = async () => {
  if (!Capacitor.isNativePlatform()) return;
  if (Capacitor.getPlatform() === 'android') {
    await PushNotifications.createChannel({
      id: 'ever_after_updates',
      name: 'Ever After List updates',
      description: 'Enquiries, messages, bookings and planning updates',
      importance: 5,
      visibility: 1,
      vibration: true,
      lights: true,
      lightColor: '#7B52C0',
    });
  }
  const permissionResult = await PushNotifications.requestPermissions();
  if (permissionResult.receive === 'granted') {
    await PushNotifications.register();
  }
};

export const addPushNotificationListeners = async () => {
  if (!Capacitor.isNativePlatform()) return;

  await PushNotifications.addListener('registration', (token: Token) => {
    console.log('[push] registration success');
    if (!signingOut) {
      registrationWork = registrationWork.then(() => saveTokenToBackend(token.value));
    }
  });

  await PushNotifications.addListener('registrationError', (error) => {
    console.error('[push] registration error', JSON.stringify(error));
  });

  await PushNotifications.addListener('pushNotificationReceived', () => {
    // Do not log message contents or personal notification data.
    window.dispatchEvent(new CustomEvent('everafterlist:push-received'));
  });

  await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const url = (action.notification.data as { url?: string })?.url;
    if (url && typeof window !== 'undefined') {
      try {
        window.sessionStorage.setItem('pending_push_navigation', url);
      } catch {
        // The live event below still handles navigation when storage is unavailable.
      }
      window.dispatchEvent(new CustomEvent('everafterlist:push-navigation', { detail: { url } }));
    }
  });
};

export const getPushNotificationToken = async (): Promise<string | null> => {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const result = await PushNotifications.requestPermissions();
    if (result.receive === 'granted') {
      await PushNotifications.register();
    }
  } catch (error) {
    console.error('[push] getPushNotificationToken error', error);
  }
  return null;
};

// Registration may happen before sign-in. Ask the OS for its current token
// again once authentication is ready, without prompting a second time.
export const refreshPushRegistration = async () => {
  if (!Capacitor.isNativePlatform()) return;
  signingOut = false;
  const permission = await PushNotifications.checkPermissions();
  if (permission.receive === 'granted') await PushNotifications.register();
};

// Older installs did not remember their token. Obtain it from the OS, never
// guess by deleting all registrations for a user/platform (other devices).
const readCurrentToken = async (): Promise<string | null> => {
  const stored = await Preferences.get({ key: tokenKey() });
  if (stored.value) return stored.value;
  const permission = await PushNotifications.checkPermissions();
  if (permission.receive !== 'granted') return null;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let finished = false;
  let listener: Awaited<ReturnType<typeof PushNotifications.addListener>> | undefined;
  try {
    return await new Promise<string>((resolve, reject) => {
      timeout = setTimeout(() => reject(new Error('Notification registration timed out')), 10000);
      void PushNotifications.addListener('registration', token => resolve(token.value))
        .then(handle => {
          if (finished) return handle.remove();
          listener = handle;
          return PushNotifications.register();
        }).catch(reject);
    });
  } finally {
    finished = true;
    clearTimeout(timeout);
    await listener?.remove();
  }
};

export const detachNativePushForSignOut = async (userId: string) => {
  if (!Capacitor.isNativePlatform()) return;
  signingOut = true;
  // Drain a registration already in flight before removing its row.
  await registrationWork;
  try {
    const token = await readCurrentToken();
    if (token) {
      const { error } = await supabase.from('native_push_tokens').delete()
        .eq('user_id', userId).eq('app', detectApp()).eq('token', token);
      if (error) throw error;
    }
    await PushNotifications.unregister();
    await Preferences.remove({ key: tokenKey() });
    await PushNotifications.removeAllDeliveredNotifications();
  } catch {
    // Keep the authenticated session so a failed/offline cleanup can be retried.
    // Registration stays paused until a later successful sign-in refresh.
    throw new Error('Could not disconnect notifications. Please reconnect and try signing out again.');
  }
};
